import { useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { Link, useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { EmptyState } from '@/components/empty-state';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/skeleton';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import { locationRoutes } from '@/lib/route-map';

// =====================================================================
// Leads — "/app/b/:siteId/pulse/leads" (WP1 Task 10). A structured,
// read-only view over the SAME form-submission store Inbox reads from
// (spec §7: "Leads = the structured potential-customer view over the same
// data ... name, source, status. Two views, one underlying store; no new
// capture system.") Controller ruling: this is a THIN view over the
// existing GET /api/forms?siteId=&status= — no new endpoint, no new
// capture system, no status mutation UI (that stays on Inbox).
//
// Every submission today comes from exactly one place — the site's contact-
// form block, the only capture mechanism that writes to formSubmissions
// (apps/renderer/src/forms.ts) — so "source" is rendered as a plain, honest
// constant here rather than a fabricated per-row distinction. The API
// response carries no field that could tell two sources apart yet; when a
// second capture mechanism exists, this is the column that would read it.
//
// Taps are NOT that second mechanism (spec §C2, 2026-08-24). A call /
// directions / website tap is an anonymous EVENT counted in Analytics Engine —
// it creates no formSubmissions row, carries no name or email, and must never
// appear as a lead here. It is reported as a count on the Pulse analytics
// screen instead. The source column therefore stays a constant.
// =====================================================================

type Status = 'new' | 'read' | 'archived' | 'spam';

interface LeadRow {
  id: string;
  dataJson: { name: string; email: string; message: string };
  status: Status;
  submittedAt: string | number;
}

const STATUS_BADGE: Record<
  Status,
  { variant: 'warning' | 'muted' | 'outline' | 'destructive'; label: string }
> = {
  new: { variant: 'warning', label: 'New' },
  read: { variant: 'muted', label: 'Read' },
  archived: { variant: 'outline', label: 'Archived' },
  spam: { variant: 'destructive', label: 'Spam' },
};

export function Leads() {
  const meState = useMe();
  const { businessId = '', locationId: siteId } = useTenantParams();
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    let cancelled = false;
    setLeads(null);
    setError(null);
    void api
      .get<{ submissions: LeadRow[] }>(locationApi(businessId, siteId, 'forms'))
      .then((res) => {
        if (!cancelled) setLeads(res.submissions);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load your leads.');
          setLeads([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Pulse"
        title="Leads"
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready') return null;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Pulse"
        title="Leads"
        description="Every potential customer who's reached out through your site, in one list."
      />

      {error ? (
        <p role="alert" className="text-sm text-danger-fg">
          {error}
        </p>
      ) : null}

      {leads === null ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      ) : leads.length === 0 ? (
        <EmptyState
          title="No leads yet"
          description="Someone fills in your contact form, they land here — name, email, what they need. Nothing yet? Check the form's easy to find."
          action={
            siteId ? (
              <Link
                to={locationRoutes.website(businessId, siteId)}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                See your website pages
              </Link>
            ) : null
          }
        />
      ) : (
        <section className="overflow-hidden rounded-xl border border-border-subtle bg-card">
          <div
            className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset"
            role="region"
            aria-label="Leads"
            tabIndex={0}
          >
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <caption className="sr-only">Leads for this site</caption>
              <thead>
                <tr className="border-b border-border-subtle text-left eyebrow">
                  <th scope="col" className="px-4 py-3">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Source
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => {
                  const badge = STATUS_BADGE[lead.status];
                  return (
                    <tr key={lead.id} className="border-b border-border-subtle last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground">
                        {lead.dataJson.name}
                      </td>
                      <td className="px-4 py-3 text-text-secondary">{lead.dataJson.email}</td>
                      <td className="px-4 py-3 text-text-secondary">Contact form</td>
                      <td className="px-4 py-3">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </PageContainer>
  );
}

export default Leads;
